import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mail, Copy, Send, Loader2, Ban, Clock, Link2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function emailStatusText(emailResult, email) {
  if (emailResult?.sent) return <>Invitation emailed to <b>{email}</b>.</>;
  if (emailResult?.skipped) return <>Email not sent — share the link below with <b>{email}</b>.</>;
  const reason = emailResult?.error ? ` (${emailResult.error})` : "";
  return <>Email could not be delivered to <b>{email}</b>{reason} — share the link below instead.</>;
}

export function InviteLinkBox({ url, emailResult, email }) {
  const copy = () => { navigator.clipboard.writeText(url); toast.success("Invite link copied"); };
  const sent = !!emailResult?.sent;
  return (
    <div className="space-y-3" data-testid="invite-link-box">
      <div className={`flex items-start gap-2 rounded-xl px-3.5 py-3 text-sm ${sent ? "bg-emerald-50 border border-emerald-100 text-emerald-800" : "bg-amber-50 border border-amber-100 text-amber-800"}`} data-testid="invite-email-status">
        {sent ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}
        <span>{emailStatusText(emailResult, email)}</span>
      </div>
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1"><Link2 className="h-3 w-3" /> Invite link · expires in 7 days</div>
        <div className="flex items-center gap-2">
          <code className="text-[11px] text-slate-700 truncate flex-1" data-testid="invite-url">{url}</code>
          <Button size="sm" variant="outline" className="h-8 gap-1.5 shrink-0" onClick={copy} data-testid="invite-copy-btn"><Copy className="h-3.5 w-3.5" /> Copy</Button>
        </div>
      </div>
    </div>
  );
}

function InviteRow({ inv, onChange }) {
  const [busy, setBusy] = useState("");
  const [fresh, setFresh] = useState(null);

  const resend = async () => {
    setBusy("resend");
    try {
      const { data } = await api.post(`/invites/${inv.id}/resend`);
      setFresh(data);
      navigator.clipboard?.writeText(data.invite_url).catch(() => {});
      (data.email_result?.sent ? toast.success : toast.warning)(data.email_result?.sent ? `Invitation re-sent to ${inv.email} · link copied` : "Email failed — fresh link copied, share it directly");
      onChange();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const revoke = async () => {
    setBusy("revoke");
    try { await api.delete(`/invites/${inv.id}`); toast.success("Invitation revoked"); onChange(); }
    catch (e) { toast.error(apiErr(e)); setBusy(""); }
  };

  const expires = new Date(inv.expires_at);
  const daysLeft = Math.max(0, Math.ceil((expires - Date.now()) / 86400000));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`invite-row-${inv.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 truncate">{inv.name}</div>
          <div className="text-xs text-slate-500 flex items-center gap-1 truncate"><Mail className="h-3 w-3" /> {inv.email}</div>
          <div className="text-xs text-slate-500 mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            {inv.property_name ? <span>{inv.property_name}</span> : <span className="text-slate-400">No lease attached</span>}
            <span className={`inline-flex items-center gap-1 ${inv.expired ? "text-rose-600 font-semibold" : ""}`} data-testid={`invite-expiry-${inv.id}`}>
              <Clock className="h-3 w-3" /> {inv.expired ? "Expired" : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}
            </span>
            {inv.send_count > 0 && <span>Sent {inv.send_count}×</span>}
            {inv.email_error && <span className="text-amber-700">Last email failed</span>}
          </div>
        </div>
        <div className="flex gap-1.5 shrink-0">
          <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={resend} disabled={!!busy} data-testid={`invite-resend-${inv.id}`}>
            {busy === "resend" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {inv.expired ? "Renew & send" : "Resend"}
          </Button>
          <Button size="sm" variant="ghost" className="h-8 text-slate-500 hover:text-rose-600" onClick={revoke} disabled={!!busy} data-testid={`invite-revoke-${inv.id}`}><Ban className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
      {fresh && <div className="mt-3"><InviteLinkBox url={fresh.invite_url} emailResult={fresh.email_result} email={inv.email} /></div>}
    </div>
  );
}

export function PendingInvites({ invites, onChange }) {
  if (!invites?.length) return null;
  return (
    <div className="mb-6 rounded-2xl border border-dashed border-primary/40 bg-emerald-50/30 p-5" data-testid="pending-invites">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-bold text-slate-900">Pending invitations <span className="ml-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5" data-testid="pending-invites-count">{invites.length}</span></h2>
        <span className="text-xs text-slate-500">Tenants appear below once they accept.</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {invites.map((inv) => <InviteRow key={inv.id} inv={inv} onChange={onChange} />)}
      </div>
    </div>
  );
}
